import axiosInstance from '../utility/axiosInstance';

/**
 * An instance as the instance list carries it: its id, its DNS entries ("<proxy id>:<name>:<port>,...") as the list shows
 * them (filled in from the platform's record of names), and its own metadata, whose `dns` is the value Nova holds.
 */
export interface DnsEntryInstance {
    id: string;
    dns?: string | null;
    metadata?: { dns?: string | null; [key: string]: any } | null;
}

/** The entries of a "<proxy id>:<name>:<port>,..." list. */
const entriesOf = (list: unknown): string[] => String(list ?? '').split(',').map((e) => e.trim()).filter(Boolean);
const idOf = (entry: string) => entry.split(':')[0];

/**
 * The console's DNS-entry delete, shared by the instance's DNS page and the domains page (ROADMAP 86), in the order the
 * MCP's delete_dns_entry uses (F6): first the entry leaves the instance's own record of its names (its `dns` metadata,
 * POST /v1/dns/update-dns), then the name itself goes (DELETE /v1/dns/proxy-hosts/:id). In the other order a refused
 * metadata write stranded the entry: the name was gone, so the same delete again answered 404 before it reached the
 * metadata, and the instance's port read as taken. The value written is the metadata's own minus the entry, never the
 * longer list the instance list fills in from the record (with enough names it is over Nova's 255 characters, which
 * update-dns refuses; never write more into the metadata than it holds), and an entry the metadata does not hold needs
 * no write. A failed name delete then leaves a name that still serves and is still listed: the same delete again
 * finishes it. Throws when either call fails, or, before anything changes, when there is no instance to edit.
 * Returns the instance's lists as they are now (`dns` as the instance list shows it, `metadataDns` as the metadata holds
 * it), so a caller removing several names in a row keeps its copy current: one computed from a stale copy would write
 * the entry just removed back.
 */
export async function deleteDnsEntry(proxyHostId: number, instance: DnsEntryInstance | undefined): Promise<{ dns: string; metadataDns: string | null }> {
    if (!instance) throw new Error('Instance not found');
    const id = String(proxyHostId);
    const held = instance.metadata?.dns ?? null;
    let metadataDns = held;
    if (entriesOf(held).some((e) => idOf(e) === id)) {
        metadataDns = entriesOf(held).filter((e) => idOf(e) !== id).join(',');
        await axiosInstance.post('/v1/dns/update-dns', {
            instanceId: instance.id,
            metadata: { dns: metadataDns },
        });
    }
    await axiosInstance.delete(`/v1/dns/proxy-hosts/${proxyHostId}`);
    return { dns: entriesOf(instance.dns).filter((e) => idOf(e) !== id).join(','), metadataDns };
}

/**
 * The instance whose record of names lists this live name: an entry with the name's id AND one of its domain names (an id
 * alone can be stale after the proxy is rebuilt and then belong to another name). Undefined for a name that is not an
 * instance's (a load balancer's or a cluster's), which is removed where it was made.
 */
export function instanceForName(instances: DnsEntryInstance[], name: { id: number; domain_names: string[] }): DnsEntryInstance | undefined {
    const names = new Set(name.domain_names.map((d) => d.toLowerCase()));
    return instances.find((inst) => String(inst.dns || '').split(',').some((entry) => {
        const [id, domain] = entry.split(':');
        return Number(id) === Number(name.id) && names.has(String(domain || '').toLowerCase());
    }));
}
